import React from 'react'
import Head from 'next/head'
import Image from 'next/image'
import { FaCheckCircle, FaExclamationTriangle, FaStar } from 'react-icons/fa'
import { s3Static } from '../../../../utils/constants'
import { Breadcrumb } from '../../../commons/Breadcrumb'
import { Faq } from '../../../commons/Faq'
import { buildBreadcrumb } from '../../../../utils/breadcrumb'
import { buildServiceHubGraph } from '../../../../utils/service-hub-schema'
import { formatPrice, fromPrice } from '../../../../utils/handyman-price-data'
import { SITE_URL } from '../../../../utils/sitemap-config'
import { googleRating, googleReviewCount } from '../../../../utils/business-info'
import {
    ELECTRICIAN_HUB,
    HANDYMAN_HUB,
    HUB_META,
    PLUMBER_HUB,
    handymanJobPages,
    jobPagePath,
} from '../../../../data/handyman-job-pages'
import { HandymanBookingPanel } from './HandymanBookingPanel'
import { PriceList } from '@/commons/service-hub/PriceList'
import { HowToBook } from '@/commons/service-hub/HowToBook'
import { RelatedLinks } from '@/commons/service-hub/RelatedLinks'

// Template for the per-job pages (power trip repair, TV mounting, clogged
// toilet, ...) under the electrician, handyman and plumber hubs.
// All copy lives in data/handyman-job-pages.js. `{from}` in that copy becomes
// the lowest live price among the page's items.
export const HandymanJobPage = ({ slug, items, handymanPriceList, requestCategorySettings, modifiedDate }) => {
    const page = handymanJobPages[slug]
    const path = jobPagePath(slug)
    const pageUrl = SITE_URL + path
    const hub = HUB_META[page.hubPath]

    const from = fromPrice(items)
    const fill = (text) => (from == null ? text.replace(/ ?(starting |start )?from \{from\}/g, '') : text.replaceAll('{from}', formatPrice(from)))

    const title = fill(page.title)
    const description = fill(page.metaDescription)
    const heroImage = s3Static + page.heroImage
    const faqs = page.faqs.map(({ question, answer }) => ({ question, answer: fill(answer) }))

    const trail = buildBreadcrumb(path, page.breadcrumbTitle)
    const schema = buildServiceHubGraph({
        pageUrl,
        pageName: title,
        description,
        trail,
        items,
        serviceName: `${page.breadcrumbTitle} Singapore`,
        serviceType: page.serviceType,
        image: heroImage,
        datePublished: '2026-09-23T08:00:00+08:00',
        modifiedDate,
    })

    // Cross-links to the two sibling hubs.
    const siblingHubs = [
        { href: ELECTRICIAN_HUB, label: 'Electrician Services', desc: 'Lighting, power points and wiring' },
        { href: PLUMBER_HUB, label: 'Plumber Services', desc: 'Leaks, choked toilets and drains' },
        { href: HANDYMAN_HUB, label: 'Handyman Services', desc: 'Mounting, curtains, locks and more' },
    ].filter((link) => link.href !== page.hubPath)
    const relatedLinks = [
        ...page.relatedSlugs.map((related) => ({
            href: jobPagePath(related),
            label: handymanJobPages[related].breadcrumbTitle,
        })),
        { href: hub.pricePage, label: hub.priceLabel, desc: 'Every job and price in one place' },
        { href: page.hubPath, label: hub.hubLabel, desc: 'All services and booking' },
        ...siblingHubs,
    ]

    const serviceLabel = hub.serviceLabel
    const whatsAppText = `Hi, I'd like to book ${page.breadcrumbTitle.toLowerCase()}.`

    return (
        <>
            <Head>
                <title>{title}</title>
                <meta charSet="utf-8" />
                <meta name="viewport" content="width=device-width, initial-scale=1.0" />
                <meta name="description" content={description} />
                <meta name="author" content="StringsSG" />
                <link rel="canonical" href={pageUrl} />
                <meta property="og:locale" content="en_US" />
                <meta property="og:type" content="website" />
                <meta property="og:url" content={pageUrl} />
                <meta property="og:title" content={title} />
                <meta property="og:description" content={description} />
                <meta property="og:site_name" content="StringsSG" />
                <meta property="og:image" content={heroImage} />
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:site" content="@stringssg" />
                <meta name="twitter:title" content={title} />
                <meta name="twitter:description" content={description} />
                <meta name="twitter:image" content={heroImage} />
                <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }} />
            </Head>

            <Breadcrumb pageTitle={page.breadcrumbTitle} jsonLd={false} />

            {/* Compact hero: the H1, price and trust line sit above the fold on a
                375px screen. The image is decorative, short and below the H1. */}
            <header className="max-w-5xl mx-auto px-4 pb-4 space-y-3">
                <h1 className="text-2xl md:text-4xl font-bold text-gray-900 leading-tight">{page.h1}</h1>
                <div className="flex flex-wrap items-center gap-2 text-sm">
                    {from != null &&
                        <span className="font-bold text-green-700 bg-green-50 border border-green-200 px-3 py-1 rounded-full">
                            From {formatPrice(from)} fixed price
                        </span>
                    }
                    <span className="flex items-center gap-1 text-gray-700 bg-white border border-gray-200 px-3 py-1 rounded-full">
                        <FaStar className="text-amber-500" aria-hidden="true" />
                        {googleRating} from {googleReviewCount} Google reviews
                    </span>
                    <span className="text-gray-700 bg-white border border-gray-200 px-3 py-1 rounded-full">Same-day slots</span>
                </div>
                <p className="text-gray-700 leading-relaxed">{fill(page.intro[0])}</p>
            </header>

            {/* Booking block. It used to be the hero-overlay widget dropped onto a dark
                band with no heading, so it read as a stray box. It now has its own
                heading, a light background and the solid card variant. */}
            <section aria-labelledby="book-heading" className="bg-linear-to-b from-orange-50 to-white border-y border-orange-100 py-8 px-3">
                <div className="max-w-3xl mx-auto text-center mb-4">
                    <h2 id="book-heading" className="text-xl md:text-2xl font-bold text-gray-900">{page.bookHeading || `Book ${page.breadcrumbTitle} Online`}</h2>
                    <p className="text-sm text-gray-600 mt-1">
                        Tap a job to see its fixed price, then pick a date and time. It takes under a minute.
                    </p>
                </div>
                <HandymanBookingPanel
                    variant="light"
                    requestCategorySettings={requestCategorySettings}
                    handymanPriceList={handymanPriceList}
                    handymanType={hub.handymanType}
                    defaultServiceType={page.bookingTab}
                    allowedServiceTypes={hub.allowedServiceTypes}
                    allowedCategories={hub.allowedCategories}
                    whatsAppText={whatsAppText}
                />
            </section>

            <div className="max-w-5xl mx-auto px-4 py-8 grid md:grid-cols-5 gap-6 items-start">
                <div className="md:col-span-3 space-y-4 text-gray-700 leading-relaxed">
                    {page.intro.slice(1).map((paragraph) => (
                        <p key={paragraph}>{fill(paragraph)}</p>
                    ))}
                    <h2 className="text-xl md:text-2xl font-bold text-gray-900 pt-2">What&apos;s Included</h2>
                    <ul className="space-y-2">
                        {page.whatsIncluded.map((line) => (
                            <li key={line} className="flex gap-2">
                                <FaCheckCircle className="shrink-0 mt-1 text-green-600" aria-hidden="true" />
                                <span>{line}</span>
                            </li>
                        ))}
                    </ul>
                </div>
                <div className="md:col-span-2 relative w-full aspect-4/3 rounded-2xl overflow-hidden">
                    <Image
                        src={heroImage}
                        fill
                        sizes="(max-width: 768px) 100vw, 400px"
                        style={{ objectFit: 'cover' }}
                        alt={page.heroAlt}
                    />
                </div>
            </div>

            {/* Problem pages (the plumbing ones) answer "how do I fix it" first:
                safe steps to try, and clear signs to stop and book a professional. */}
            {page.diy &&
                <section className="max-w-5xl mx-auto px-4 pb-10">
                    <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4">{page.diy.heading}</h2>
                    <div className="grid md:grid-cols-5 gap-5 items-start">
                        <ol className="md:col-span-3 space-y-3">
                            {page.diy.steps.map((step, i) => (
                                <li key={step} className="flex gap-3">
                                    <span className="shrink-0 flex items-center justify-center w-7 h-7 rounded-full bg-orange-100 text-orange-700 text-sm font-bold">{i + 1}</span>
                                    <span className="text-gray-700 leading-relaxed">{step}</span>
                                </li>
                            ))}
                        </ol>
                        <div className="md:col-span-2 bg-red-50 border border-red-200 rounded-2xl p-5">
                            <h3 className="flex items-center gap-2 font-bold text-red-800 mb-2">
                                <FaExclamationTriangle aria-hidden="true" />
                                Stop and call a {serviceLabel} if
                            </h3>
                            <ul className="list-disc pl-5 space-y-1.5 text-sm text-red-900 leading-relaxed">
                                {page.diy.stopIf.map((line) => <li key={line}>{line}</li>)}
                            </ul>
                        </div>
                    </div>
                </section>
            }

            {items.length > 0 &&
                <PriceList
                    items={items}
                    title={`${page.breadcrumbTitle} Price in Singapore`}
                    subtitle="Fixed prices per job. You see the total before you confirm the booking."
                    moreHref={hub.pricePage}
                    moreLabel={`See all ${serviceLabel} prices`}
                />
            }

            <section className="max-w-5xl mx-auto px-4 py-10 grid md:grid-cols-2 gap-6">
                <div className="bg-white border border-gray-200 rounded-2xl p-5">
                    <h2 className="text-lg md:text-xl font-bold text-gray-900 mb-3">{page.signsHeading}</h2>
                    <ul className="list-disc pl-5 space-y-2 text-sm text-gray-700 leading-relaxed">
                        {page.signs.map((line) => <li key={line}>{line}</li>)}
                    </ul>
                </div>
                <div className="bg-white border border-gray-200 rounded-2xl p-5">
                    <h2 className="text-lg md:text-xl font-bold text-gray-900 mb-3">What Affects the Price</h2>
                    <ul className="list-disc pl-5 space-y-2 text-sm text-gray-700 leading-relaxed">
                        {page.priceFactors.map((line) => <li key={line}>{line}</li>)}
                    </ul>
                </div>
            </section>

            <HowToBook serviceLabel={serviceLabel} />

            <section className="max-w-5xl mx-auto">
                <Faq title={`${page.breadcrumbTitle} FAQs`} questionsAnswers={faqs} showSearch={false} />
            </section>

            <RelatedLinks title="Related Services" links={relatedLinks} />
            {/* Room for the sticky Book / WhatsApp bar on mobile. */}
            <div aria-hidden="true" className="h-20 md:hidden" />
        </>
    )
}
